import { useQuery } from '@tanstack/react-query';
import { AlertCircle, BookOpen, Check, ChevronDown, Globe, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '@/contexts/AuthContext';
import { languageOptions } from '@/constants/languageOptions';
import { getLanguageCode } from '@/utils/languageMapper';
import {
  AssessmentAttemptProvider,
  useAssessmentAttemptState,
} from '@/contexts/AssessmentAttemptContext';

// Services
import { courseService } from '@/services/course.service';
import trackingService, { DetailedProgress } from '@/services/tracking.service';
import { Leaderboardservice, MyCourseMetricsResponse, EarnedBadge } from '@/services/leaderboard.service';
import { businessService } from '@/services/business.service';

// Types
import {
  ContentItem,
  ModuleContentTree,
} from '@/types/ai-learn.types';

// Components
import UnifiedPlayerView from '@/components/ai-learn/UnifiedPlayerView';
import TabbedSidebar from '@/components/ai-learn/TabbedSidebar';
import CourseCompletionCelebration from '@/components/ai-learn/CourseCompletionCelebration';
import TermsAgreementModal from '@/components/ai-learn/TermsAgreementModal';
import type { CompletionConditionStatus } from '@/services/course.service';
import BadgePopup from '@/components/ai-learn/BadgePopup';
interface AILearnPageProps {
  isLearner?: boolean;
}

// The server rejects tracking writes with this code once an assignment is revoked.
const isRevokedError = (err: any): boolean =>
  err?.response?.data?.error?.error_code === 'course_revoked';

/**
 * Wraps the player so the assessment player, several layers down, can tell the
 * language switcher when a paper is open. Kept as a thin wrapper so the page
 * body below is unchanged.
 */
export default function AILearnPage(props: AILearnPageProps) {
  return (
    <AssessmentAttemptProvider>
      <AILearnPageInner {...props} />
    </AssessmentAttemptProvider>
  );
}

function AILearnPageInner({ isLearner = false }: AILearnPageProps) {
  const { courseId } = useParams<{ courseId: string }>();

  // Which language version the learner is reading. Held in the URL so a refresh
  // keeps it and the page can be shared as a link — and so courseId stays the
  // MASTER course in every language, which is what keeps progress on one record.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeLanguageCode = searchParams.get('language') || undefined;

  // True while the learner has an assessment open. The language is held
  // steady until they submit — see AssessmentAttemptContext for why.
  const { isAttemptInProgress } = useAssessmentAttemptState();

  const { user } = useAuth();
  const navigate = useNavigate();

  // The player fills the screen and scrolls inside its own panes, so the page
  // behind it must not scroll. When it could, a swipe past the end of a lesson
  // moved the whole page on phones (and showed/hid the browser's address bar),
  // pushing Previous / Next off screen or leaving a gap under it.
  useEffect(() => {
    const html = document.documentElement;
    const previousOverflow = html.style.overflow;
    html.style.overflow = 'hidden';
    return () => {
      html.style.overflow = previousOverflow;
    };
  }, []);

  // Below lg the player must end exactly at the bottom of the visible screen,
  // where Previous / Next sit. 100dvh alone isn't reliable on phones: Chrome
  // on Android can report it short of the real viewport on first load (Next /
  // Previous then float with a gap under them until something — like going
  // fullscreen and back — makes the browser re-measure). So measure it: from
  // the player's own top edge to the bottom of the window, again on every
  // resize (address bar showing/hiding, rotation, leaving fullscreen).
  const [playerRoot, setPlayerRoot] = useState<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!playerRoot) return;
    const belowLg = window.matchMedia('(max-width: 1023.98px)');
    const fit = () => {
      if (!belowLg.matches) {
        playerRoot.style.height = '';
        return;
      }
      // The page is scroll-locked; make sure it isn't left scrolled either.
      if (window.scrollY) window.scrollTo(0, 0);
      const top = playerRoot.getBoundingClientRect().top;
      playerRoot.style.height = `${Math.max(0, window.innerHeight - top)}px`;
    };
    fit();
    // The address bar settles a moment after load; catch it.
    const settle = window.setTimeout(fit, 300);
    window.addEventListener('resize', fit);
    window.visualViewport?.addEventListener('resize', fit);
    document.addEventListener('fullscreenchange', fit);
    belowLg.addEventListener('change', fit);
    return () => {
      window.clearTimeout(settle);
      window.removeEventListener('resize', fit);
      window.visualViewport?.removeEventListener('resize', fit);
      document.removeEventListener('fullscreenchange', fit);
      belowLg.removeEventListener('change', fit);
      playerRoot.style.height = '';
    };
  }, [playerRoot]);

  // Core state
  const [selectedModule, setSelectedModule] = useState<any>(null);
  const [selectedModuleContent, setSelectedModuleContent] = useState<ModuleContentTree | null>(null);
  const [selectedContentItem, setSelectedContentItem] = useState<ContentItem | null>(null);
  const loadingModuleContent = false;
  const [showingCourseOverview, setShowingCourseOverview] = useState(false);
  // Phones/tablets (< lg): which panel is open over the player — the course
  // navigation sheet or the full-screen AI assistant. See TabbedSidebar.
  const [mobilePanel, setMobilePanel] = useState<'content' | 'ai' | null>(null);

  // Cache for module content
  const [moduleContentCache, setModuleContentCache] = useState<{ [moduleId: string]: ModuleContentTree }>({});

  // UI state - Initialize after course loads (see useEffect below)
  const [expandedModules, setExpandedModules] = useState<{ [key: string]: boolean }>({});

  // Media player state
  const [currentVideoScene, setCurrentVideoScene] = useState(0);
  const [currentAudioScene, setCurrentAudioScene] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [videoPlaybackRate, setVideoPlaybackRate] = useState(1);
  const [audioPlaybackRate, setAudioPlaybackRate] = useState(1);
  const [videoVolume, setVideoVolume] = useState(0.8);
  const [audioVolume, setAudioVolume] = useState(0.8);
  const [autoPlayNextScene, setAutoPlayNextScene] = useState(true);

  // Tracking state (API response = single source of truth)
  const [trackingData, setTrackingData] = useState<DetailedProgress | null>(null);
  const [metricsData, setMetricsData] = useState<MyCourseMetricsResponse | null>(null);
  const [localTimeAccumulator, setLocalTimeAccumulator] = useState(0);
  const [isCourseCompleted, setIsCourseCompleted] = useState(false);
  const [completingCourse, setCompletingCourse] = useState(false);
  // Terms & Conditions completion gate
  const [pendingTermsCondition, setPendingTermsCondition] = useState<CompletionConditionStatus | null>(null);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [acceptingTerms, setAcceptingTerms] = useState(false);
  const [trackingLoaded, setTrackingLoaded] = useState(false);

  // Popup state — course completion, badge earned
  const [showCourseCompletion, setShowCourseCompletion] = useState(false);
  const [showBadgePopup, setShowBadgePopup] = useState(false);
  const [pendingBadges, setPendingBadges] = useState<EarnedBadge[]>([]);
  const [acknowledgedBadgeIds, setAcknowledgedBadgeIds] = useState<Set<string>>(new Set());

  // Refs to avoid stale closures in intervals
  const tickIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const selectedContentItemRef = useRef<ContentItem | null>(null);
  const trackingDataRef = useRef<DetailedProgress | null>(null);
  const localTimeAccumulatorRef = useRef(0);
  const isCourseCompletedRef = useRef(false);
  const isVideoPlayingRef = useRef(false);
  const prevCourseCompletedRef = useRef(false);
  const hasPendingRequirementsRef = useRef(false);
  // Resume-on-load runs once; see the resume effect below.
  const hasResumedRef = useRef(false);
  // Set once the server reports the course was revoked — stops every further flush.
  const isRevokedRef = useRef(false);

  // Fetch business settings for course completion threshold — refetch on every course load
  const { data: businessSettings } = useQuery({
    queryKey: ['businessSettings', courseId],
    queryFn: () => businessService.getBusinessSettings(),
  });
  const completionThreshold = (businessSettings?.data?.course_completion_threshold || 80) / 100;

  // Assessments this course requires a pass on before it can complete (its
  // completion_config). Refetched after every assessment submission so passing the
  // last one clears the hold. all_passed is true when the course has no
  // requirements, so courses without a config behave exactly as before.
  const { data: completionRequirements, refetch: refetchCompletionRequirements } = useQuery({
    queryKey: ['course-completion-requirements', courseId],
    queryFn: () => courseService.getCourseCompletionRequirements(courseId!),
    enabled: !!courseId && isLearner,
  });
  const pendingRequirements = completionRequirements?.requirements?.filter(r => !r.passed) || [];
  const hasPendingRequirements = pendingRequirements.length > 0;
  const completionRequirementsRef = useRef(completionRequirements);
  useEffect(() => { completionRequirementsRef.current = completionRequirements; }, [completionRequirements]);

  // Custom completion gate (assessments + custom conditions like Terms & Conditions).
  // A pending T&C blocks completion the same way an unpassed required assessment does,
  // and must be presented to the learner once they finish all content.
  const { data: completionGate, refetch: refetchCompletionGate } = useQuery({
    queryKey: ['course-completion-gate', courseId],
    queryFn: () => courseService.getCompletionGate(courseId!),
    enabled: !!courseId && isLearner,
  });
  const pendingTerms =
    completionGate?.conditions?.find(c => c.type === 'terms_and_conditions' && !c.is_satisfied) || null;
  const hasPendingTerms = !!pendingTerms;
  const hasPendingTermsRef = useRef(false);
  const suppressTermsModalRef = useRef(false);

  // Fetch ALL course data on mount using single complete API call
  const { data: courseData, isLoading, error } = useQuery<any>({
    // The language is part of the cache key. Without it, switching to Hindi
    // would return the English course straight from the react-query cache and
    // never call the API — the same trap the admin module cache fell into.
    queryKey: ['course-complete-data-v2', courseId, activeLanguageCode ?? 'master'],
    queryFn: async () => {
      if (!courseId) return null;

      // Single API call to get complete course structure
      const completeCourseData = await courseService.getCompleteCourseData(courseId, activeLanguageCode);

      // Transform API response to our internal structure
      const contentCache: { [key: string]: ModuleContentTree } = {};

      completeCourseData.modules.forEach((apiModule: any) => {
        const contentItems: ContentItem[] = [];

        // Add module pre-assessments (metadata only)
        if (apiModule.pre_assessments && apiModule.pre_assessments.length > 0) {
          apiModule.pre_assessments.forEach((assessment: any) => {
            contentItems.push({
              _id: `assessment_${assessment.id}_PRE_MODULE_${apiModule.id}`,
              title: assessment.title || 'Pre-Assessment',
              type: 'assessment',
              description: assessment.description,
              duration: assessment.time_limit_minutes
                ? { minutes: assessment.time_limit_minutes }
                : undefined,
              estimated_time: assessment.time_limit_minutes
                ? { minutes: assessment.time_limit_minutes }
                : undefined,
              assessment_id: assessment.id,
              assessment_type: 'PRE',
              assessment_level: 'MODULE',
            });
          });
        }

        // Process lessons (content items)
        if (apiModule.lessons && apiModule.lessons.length > 0) {
          apiModule.lessons.forEach((lesson: any) => {
            // Add lesson pre-assessments (metadata only)
            if (lesson.pre_assessments && lesson.pre_assessments.length > 0) {
              lesson.pre_assessments.forEach((assessment: any) => {
                contentItems.push({
                  _id: `assessment_${assessment.id}_PRE_CONTENT_${lesson.id}`,
                  title: assessment.title || 'Pre-Assessment',
                  type: 'assessment',
                  description: assessment.description,
                  duration: assessment.time_limit_minutes
                    ? { minutes: assessment.time_limit_minutes }
                    : undefined,
                  estimated_time: assessment.time_limit_minutes
                    ? { minutes: assessment.time_limit_minutes }
                    : undefined,
                  assessment_id: assessment.id,
                  assessment_type: 'PRE',
                  assessment_level: 'CONTENT_ITEM',
                });
              });
            }

            // Add the actual lesson/content item
            contentItems.push({
              _id: lesson.id,
              title: lesson.title,
              type: lesson.type?.toLowerCase() || 'html',
              description: lesson.description,
              duration: lesson.estimated_time || { hours: 0, minutes: lesson.durationMinutes || 15 },
              estimated_time: lesson.estimated_time || { hours: 0, minutes: lesson.durationMinutes || 15 },
              media_assets: {
                s3_html_url: lesson.s3_html_url,
                s3_audio_url: lesson.s3_audio_url,
                s3_video_url: lesson.s3_video_url,
                // Captioned variant — without this the CC toggle never appears on the player.
                s3_captioned_video_url: lesson.s3_captioned_video_url,
              },
              html_content: lesson.html_content,
              narration: lesson.narration,
              // Uploaded (PDF/video) content: the renderer embeds an uploaded PDF
              // directly in a non-sandboxed iframe (Chrome blocks the PDF viewer
              // inside a sandboxed frame).
              content_source: lesson.content_source,
              uploaded_file_url: lesson.uploaded_file_url,
              uploaded_file_type: lesson.uploaded_file_type,
            });

            // Add lesson post-assessments (metadata only)
            if (lesson.post_assessments && lesson.post_assessments.length > 0) {
              lesson.post_assessments.forEach((assessment: any) => {
                contentItems.push({
                  _id: `assessment_${assessment.id}_POST_CONTENT_${lesson.id}`,
                  title: assessment.title || 'Post-Assessment',
                  type: 'assessment',
                  description: assessment.description,
                  duration: assessment.time_limit_minutes
                    ? { minutes: assessment.time_limit_minutes }
                    : undefined,
                  estimated_time: assessment.time_limit_minutes
                    ? { minutes: assessment.time_limit_minutes }
                    : undefined,
                  assessment_id: assessment.id,
                  assessment_type: 'POST',
                  assessment_level: 'CONTENT_ITEM',
                });
              });
            }
          });
        }

        // Add module post-assessments (metadata only)
        if (apiModule.post_assessments && apiModule.post_assessments.length > 0) {
          apiModule.post_assessments.forEach((assessment: any) => {
            contentItems.push({
              _id: `assessment_${assessment.id}_POST_MODULE_${apiModule.id}`,
              title: assessment.title || 'Post-Assessment',
              type: 'assessment',
              description: assessment.description,
              duration: assessment.time_limit_minutes
                ? { minutes: assessment.time_limit_minutes }
                : undefined,
              estimated_time: assessment.time_limit_minutes
                ? { minutes: assessment.time_limit_minutes }
                : undefined,
              assessment_id: assessment.id,
              assessment_type: 'POST',
              assessment_level: 'MODULE',
            });
          });
        }

        // Build module content cache entry
        contentCache[apiModule.id] = {
          module: {
            _id: apiModule.id,
            title: apiModule.title,
            description: apiModule.description,
            module_number: apiModule.order,
            estimated_duration: {
              hours: Math.floor((apiModule.durationMinutes || 0) / 60),
              minutes: (apiModule.durationMinutes || 0) % 60
            },
            content_items: contentItems
          }
        };
      });

      // Transform course data to match existing structure
      const transformedCourse = {
        _id: completeCourseData.id,
        id: completeCourseData.id,
        title: completeCourseData.title,
        description: completeCourseData.description,
        difficulty: completeCourseData.difficulty,
        category: completeCourseData.category,
        subcategory: completeCourseData.subcategory || '',
        instructor: completeCourseData.instructor,
        estimated_duration: {
          hours: completeCourseData.duration?.estimated_hours || 0,
          minutes: 0,
          total_minutes: (completeCourseData.duration?.estimated_hours || 0) * 60
        },
        modules: completeCourseData.modules.map((m: any) => ({
          _id: m.id,
          id: m.id,
          title: m.title,
          description: m.description,
          module_number: m.order,
          order: m.order,
          estimated_duration: {
            hours: Math.floor((m.durationMinutes || 0) / 60),
            minutes: (m.durationMinutes || 0) % 60
          },
          lessons: m.lessons || [],
          pre_assessments: m.pre_assessments || [],
          post_assessments: m.post_assessments || []
        })),
        pre_assessment: completeCourseData.pre_assessment,
        post_assessment: completeCourseData.post_assessment,
        metadata: completeCourseData.metadata,
        requirements: completeCourseData.requirements,
        certification: completeCourseData.certification,
        structure: completeCourseData.structure,
      };

      return {
        course: transformedCourse,
        moduleContentCache: contentCache,
        // Carried through so the header can offer a language menu without a
        // second request. Empty when the course has no finished translations.
        availableLanguages: completeCourseData.available_languages || [],
        // The ORIGINAL course's language, sent even when a translation is being
        // served.
        masterLanguageCode: completeCourseData.master_language_code,
        // Which language THIS payload is. Not the same as the language in the
        // URL: between picking a language and its data arriving, the URL has
        // already changed while the content on screen has not.
        loadedLanguageCode: activeLanguageCode ?? 'master',
      };
    },
    enabled: !!courseId
  });

  const course = courseData?.course;
  const availableLanguages: Array<{ language_code: string; language: string }> =
    courseData?.availableLanguages || [];

  /** Live watch position, updated as the learner watches. A ref, not state:
   *  it changes several times a second and must not re-render the page. */
  const videoProgressRef = useRef<{ ratio: number; playing: boolean }>({ ratio: 0, playing: false });

  /** The position to resume at after a language switch. Cleared once used, so
   *  moving to another lesson starts at the beginning as it always did. */
  const [videoResume, setVideoResume] = useState<{ ratio: number; playing: boolean } | undefined>();

  /**
   * Switch the language version being read. Only the query string changes — the
   * course id in the URL stays the master's, so the learner's enrolment,
   * progress and completion are untouched.
   */
  const handleSwitchLanguage = (languageCode?: string) => {
    // Carry the watch position over as a RATIO. The same lesson is a different
    // length in every language — Kannada runs ~27% longer than English — so the
    // raw timestamp would drop the learner on content they have not reached.
    // Lives in a ref because the player remounts on the switch and loses state.
    setVideoResume(
      videoProgressRef.current.ratio > 0 ? { ...videoProgressRef.current } : undefined
    );

    resetMediaPlayerState();
    const next = new URLSearchParams(searchParams);
    if (languageCode) {
      next.set('language', languageCode);
    } else {
      next.delete('language');
    }
    setSearchParams(next, { replace: true });
  };

  // -- language menu ---------------------------------------------------------
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const languageMenuRef = useRef<HTMLDivElement>(null);

  // Close on an outside click. Bound only while the menu is OPEN, so a closed
  // menu adds no document-level listener to every course page.
  useEffect(() => {
    if (!isLanguageMenuOpen) return;
    const onDocMouseDown = (event: MouseEvent) => {
      if (languageMenuRef.current && !languageMenuRef.current.contains(event.target as Node)) {
        setIsLanguageMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [isLanguageMenuOpen]);

  /** Human label for a language row. */
  const languageLabelFor = (lang: { language_code: string; language?: string }) =>
    languageOptions.find((o) => o.value === lang.language_code)?.label ||
    lang.language ||
    lang.language_code;

  /**
   * The original entry is named by its LANGUAGE — "English (US)", not
   * "Original".
   */
  const masterLanguageCode: string | undefined = courseData?.masterLanguageCode;
  const originalLanguageLabel =
    (masterLanguageCode &&
      languageOptions.find((o) => o.value === getLanguageCode(masterLanguageCode))?.label) ||
    languageOptions.find(
      (o) => o.value === ((course as any)?.language_code || (course as any)?.language)
    )?.label ||
    'Original';

  const activeLanguageLabel = activeLanguageCode
    ? languageLabelFor(
        availableLanguages.find(
          (l) => l.language_code?.toLowerCase() === activeLanguageCode.toLowerCase()
        ) || { language_code: activeLanguageCode }
      )
    : originalLanguageLabel;

  // A SCORM course is one whose modules contain a content item of type 'scorm'.
  // SCORM courses should always open at the course overview (no resume into a module).
  const isScormCourse = (() => {
    const cache = courseData?.moduleContentCache || {};
    return Object.values(cache).some((m: any) =>
      (m?.module?.content_items || []).some((ci: any) => ci?.type === 'scorm')
    );
  })();

  // Auto-collapse main sidebar on mount (temporary for this page only)
  useEffect(() => {
    // Save the current sidebar state
    const sidebarKey = isLearner ? 'learnerSidebarCollapsed' : 'adminSidebarCollapsed';
    const previousSidebarState = localStorage.getItem(sidebarKey);

    // Collapse the sidebar for AI Learn page
    localStorage.setItem(sidebarKey, 'true');
    // Trigger a storage event to update the layout
    window.dispatchEvent(new Event('storage'));

    // Cleanup: Restore previous state when component unmounts
    return () => {
      if (previousSidebarState !== null) {
        localStorage.setItem(sidebarKey, previousSidebarState);
      } else {
        localStorage.removeItem(sidebarKey);
      }
      window.dispatchEvent(new Event('storage'));
    };
  }, [isLearner]);

  // One-time cleanup of the OLD localStorage-based resume keys (`nav-state-*`). Resume is now
  // API-driven (last_accessed_at), so these are stale leftovers. We only remove keys with the
  // `nav-state-` prefix — every other localStorage entry (sidebar, auth, notifications, etc.)
  // is left untouched.
  useEffect(() => {
    try {
      Object.keys(localStorage)
        .filter((k) => k.startsWith('nav-state-'))
        .forEach((k) => localStorage.removeItem(k));
    } catch {
      /* ignore */
    }
  }, []);

  // Initialize expanded modules (all collapsed by default - user must click to expand)
  useEffect(() => {
    if (course?.modules?.length > 0) {
      const allCollapsed: { [key: string]: boolean } = {};

      // Keep all modules collapsed initially
      course.modules.forEach((module: any) => {
        allCollapsed[module._id] = false;
      });

      // Keep course pre-assessment collapsed
      if (course.pre_assessment?.id) {
        allCollapsed[`course_pre_assessment_${course.pre_assessment.id}`] = false;
      }

      // Keep course post-assessment collapsed
      if (course.post_assessment?.id) {
        allCollapsed[`course_assessment_${course.post_assessment.id}`] = false;
      }

      // A language switch replaces `course`, so this effect re-runs and would
      // collapse the sidebar mid-lesson — the learner lost sight of where they
      // were. The module being read stays open across the switch.
      if (selectedModule?._id) {
        allCollapsed[selectedModule._id] = true;
      }

      setExpandedModules(allCollapsed);
    }
    // selectedModule is deliberately NOT a dependency: this runs when the
    // course loads or its language changes, and only reads the current module.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course?.modules, course?.pre_assessment?.id, course?.post_assessment?.id]);

  // Initialize moduleContentCache from pre-loaded data and sync selected module/content item on language switch
  useEffect(() => {
    if (!courseData) return;

    if (courseData.moduleContentCache) {
      setModuleContentCache(courseData.moduleContentCache);
    }

    // Keep selectedModule, selectedModuleContent, and selectedContentItem in sync with new courseData (e.g. on language switch)
    if (selectedModule) {
      const moduleId = selectedModule._id || selectedModule.id;
      const updatedModule = courseData.course?.modules?.find(
        (m: any) => (m._id || m.id) === moduleId
      );
      if (updatedModule) {
        setSelectedModule(updatedModule);
      }
      if (courseData.moduleContentCache?.[moduleId]) {
        setSelectedModuleContent(courseData.moduleContentCache[moduleId]);
      }
    }

    if (selectedContentItem) {
      let foundItem: ContentItem | null = null;
      for (const modId of Object.keys(courseData.moduleContentCache || {})) {
        const items = courseData.moduleContentCache[modId]?.module?.content_items;
        const matching = items?.find((ci: ContentItem) => ci._id === selectedContentItem._id);
        if (matching) {
          foundItem = matching;
          break;
        }
      }
      if (foundItem) {
        setSelectedContentItem(foundItem);
      }
    }
  }, [courseData]);

  // What the player shows, read from the LOADED language in the same render.
  // The effect above syncs state one render late; on a first-time switch the
  // player mounted with the old-language lesson in that gap (fixed by a reload).
  const displayedModule = useMemo(() => {
    if (!selectedModule) return selectedModule;
    const moduleId = selectedModule._id || selectedModule.id;
    return courseData?.course?.modules?.find((m: any) => (m._id || m.id) === moduleId) || selectedModule;
  }, [courseData, selectedModule]);

  const displayedModuleContent = useMemo(() => {
    const moduleId = selectedModule?._id || selectedModule?.id;
    return (moduleId && courseData?.moduleContentCache?.[moduleId]) || selectedModuleContent;
  }, [courseData, selectedModule, selectedModuleContent]);

  const displayedContentItem = useMemo(() => {
    if (!selectedContentItem) return selectedContentItem;
    for (const entry of Object.values(courseData?.moduleContentCache || {}) as ModuleContentTree[]) {
      const match = entry?.module?.content_items?.find((ci: ContentItem) => ci._id === selectedContentItem._id);
      if (match) return match;
    }
    return selectedContentItem;
  }, [courseData, selectedContentItem]);

  // Resume where the learner left off — driven by tracking from the API (NOT localStorage),
  // for BOTH AI and SCORM courses. We pick the lesson the learner most recently accessed
  // (max last_accessed_at across all modules) and open it; if there's nothing to resume
  // (fresh start, or tracking not loaded yet) we show the course overview.
  useEffect(() => {
    if (!course?.modules?.length || !courseId || selectedModule) return;
    // Resume is a one-time thing on load. Without this guard the effect re-fires
    // whenever selectedModule goes back to null — which is exactly what clicking
    // "Overview" does — and immediately re-selects the last lesson, making the
    // overview impossible to reach.
    if (hasResumedRef.current) return;
    hasResumedRef.current = true;

    const cache = courseData?.moduleContentCache || moduleContentCache;

    // Most recently accessed lesson = where the learner left off.
    let target: { moduleId: string; lessonId: string; at: number } | null = null;
    for (const mp of (trackingData?.module_progress || [])) {
      for (const lp of (mp.lesson_progress || [])) {
        const at = lp.last_accessed_at ? new Date(lp.last_accessed_at).getTime() : 0;
        if (at > 0 && (!target || at > target.at)) {
          target = { moduleId: mp.module_id, lessonId: lp.lesson_id, at };
        }
      }
    }

    if (target) {
      const mod = course.modules.find((m: any) => m._id === target!.moduleId || m.id === target!.moduleId);
      if (mod && cache[mod._id]) {
        const contentItem = cache[mod._id].module?.content_items?.find(
          (ci: ContentItem) => ci._id === target!.lessonId
        );
        setSelectedModule(mod);
        setSelectedModuleContent(cache[mod._id]);
        if (contentItem) setSelectedContentItem(contentItem);
        setExpandedModules(prev => ({ ...prev, [mod._id]: true }));
        setShowingCourseOverview(false);
        return;
      }
    }

    setShowingCourseOverview(true);
  }, [course, courseId, courseData, selectedModule, trackingData, moduleContentCache]);

  // === TRACKING: Keep refs in sync with state ===
  useEffect(() => { selectedContentItemRef.current = selectedContentItem; }, [selectedContentItem]);
  useEffect(() => { trackingDataRef.current = trackingData; }, [trackingData]);
  useEffect(() => { localTimeAccumulatorRef.current = localTimeAccumulator; }, [localTimeAccumulator]);
  useEffect(() => { isCourseCompletedRef.current = isCourseCompleted; }, [isCourseCompleted]);
  useEffect(() => { isVideoPlayingRef.current = isVideoPlaying; }, [isVideoPlaying]);
  useEffect(() => { hasPendingRequirementsRef.current = hasPendingRequirements; }, [hasPendingRequirements]);
  useEffect(() => { hasPendingTermsRef.current = hasPendingTerms; }, [hasPendingTerms]);

  // === TRACKING: Helper — convert estimated_time to seconds ===
  const estimatedTimeToSeconds = (et?: { hours?: number; minutes?: number }): number => {
    if (!et) return 0;
    return ((et.hours || 0) * 3600) + ((et.minutes || 0) * 60);
  };

  // === TRACKING: Build bulk payload from current tracking state ===
  const buildBulkPayload = useCallback(() => {
    const td = trackingDataRef.current;
    if (!td || !courseId) return null;

    const active_content_id = selectedContentItemRef.current?._id || null;
    const active_content_type = selectedContentItemRef.current?.type || null;
    const accumulated = localTimeAccumulatorRef.current;
    const now = new Date().toISOString();

    // Deep-clone module_progress to avoid mutating state
    const module_progress = (td.module_progress || []).map(mod => {
      // A lesson deleted from the course can never be opened, so tracking it holds the module
      // at IN_PROGRESS forever. Only filter once this module's content is cached.
      const cached_items = moduleContentCache[mod.module_id]?.module?.content_items;
      const live_lesson_ids = cached_items?.length
        ? new Set(cached_items.map((ci: ContentItem) => ci._id))
        : null;
      const tracked_lessons = live_lesson_ids
        ? (mod.lesson_progress || []).filter(lp => live_lesson_ids.has(lp.lesson_id))
        : (mod.lesson_progress || []);

      const lesson_progress = tracked_lessons.map(lesson => {
        let time_spent = lesson.time_spent_seconds || 0;
        let status = lesson.status;

        // Only add accumulated time if lesson is NOT already completed
        if (status !== 'COMPLETED' && active_content_id === lesson.lesson_id && active_content_type !== 'assessment') {
          time_spent += accumulated;
        }

        // Auto-complete: if time_spent >= threshold% of estimated_time → COMPLETED
        if (status !== 'COMPLETED') {
          // Find estimated_time from moduleContentCache
          const module_content = moduleContentCache[mod.module_id];
          const content_item = module_content?.module?.content_items?.find(
            (ci: ContentItem) => ci._id === lesson.lesson_id
          );
          const estimated_seconds = estimatedTimeToSeconds(content_item?.estimated_time);
          const completion_threshold = estimated_seconds * completionThreshold;
          if (estimated_seconds > 0 && time_spent >= completion_threshold) {
            status = 'COMPLETED';
          } else if (time_spent > 0 && status === 'NOT_STARTED') {
            status = 'IN_PROGRESS';
          }
        }

        return {
          lesson_id: lesson.lesson_id,
          lesson_title: lesson.lesson_title,
          status,
          progress_percentage: status === 'COMPLETED' ? 100 : lesson.progress_percentage || 0,
          time_spent_seconds: time_spent,
          started_at: lesson.started_at || (time_spent > 0 ? now : undefined),
          last_accessed_at: active_content_id === lesson.lesson_id ? now : lesson.last_accessed_at,
          completed_at: status === 'COMPLETED' ? (lesson.completed_at || now) : undefined,
          attempts: lesson.attempts || 0,
          bookmarked: lesson.bookmarked || false,
        };
      });

      // Module completes when all non-assessment lessons are COMPLETED
      const non_assessment_lessons = lesson_progress.filter(lp => {
        // Check if this lesson is an assessment by looking at moduleContentCache
        const module_content = moduleContentCache[mod.module_id];
        const content_item = module_content?.module?.content_items?.find(
          (ci: ContentItem) => ci._id === lp.lesson_id
        );
        return content_item?.type !== 'assessment';
      });

      // A module with no lessons authored has nothing to complete and would block the course
      // forever — complete it once the learner actually opens it.
      const is_open_module = selectedModule?._id === mod.module_id || selectedModule?.id === mod.module_id;

      let mod_status = mod.status;
      if (non_assessment_lessons.length === 0) {
        if (mod.status === 'COMPLETED' || is_open_module) mod_status = 'COMPLETED';
      } else if (non_assessment_lessons.every(lp => lp.status === 'COMPLETED')) {
        mod_status = 'COMPLETED';
      } else if (lesson_progress.some(lp => lp.status === 'IN_PROGRESS' || lp.status === 'COMPLETED')) {
        if (mod_status === 'NOT_STARTED') mod_status = 'IN_PROGRESS';
      }

      const completed_count = lesson_progress.filter(lp => lp.status === 'COMPLETED').length;
      const total_count = lesson_progress.length;
      const mod_progress_pct = total_count > 0 ? Math.round((completed_count / total_count) * 100) : 0;
      const mod_time = lesson_progress.reduce((sum, lp) => sum + lp.time_spent_seconds, 0);

      return {
        module_id: mod.module_id,
        module_title: mod.module_title,
        status: mod_status,
        progress_percentage: mod_status === 'COMPLETED' ? 100 : mod_progress_pct,
        time_spent_seconds: mod_time,
        started_at: mod.started_at || (mod_time > 0 ? now : undefined),
        last_accessed_at: now,
        completed_at: mod_status === 'COMPLETED' ? (mod.completed_at || now) : undefined,
        lesson_progress,
      };
    });

    // Course completes when all modules are COMPLETED — and, if the course has a
    // completion_config, once every required assessment has been passed. The server
    // enforces the same rule and will downgrade a premature COMPLETED, but mirroring
    // it here keeps the celebration popup from flashing before the hold is applied.
    const all_modules_completed = module_progress.length > 0 && module_progress.every(m => m.status === 'COMPLETED');
    let course_status = td.status;
    if (all_modules_completed && !hasPendingRequirementsRef.current && !hasPendingTermsRef.current) {
      course_status = 'COMPLETED';
    } else if (module_progress.some(m => m.status === 'IN_PROGRESS' || m.status === 'COMPLETED')) {
      if (course_status === 'NOT_STARTED') course_status = 'IN_PROGRESS';
    }

    const completed_modules_count = module_progress.filter(m => m.status === 'COMPLETED').length;
    const total_time = module_progress.reduce((sum, m) => sum + m.time_spent_seconds, 0);

    // Same formula as the server: (completed lessons + passed required assessments)
    // / (all lessons + required assessments), floored; 100 only once COMPLETED.
    const all_lessons = module_progress.flatMap(m => m.lesson_progress);
    const requirements = completionRequirementsRef.current?.requirements || [];
    const total_units = all_lessons.length + requirements.length;
    const done_units = all_lessons.filter(lp => lp.status === 'COMPLETED').length
      + requirements.filter(r => r.passed).length;
    const raw_progress_pct = total_units > 0
      ? Math.floor((done_units * 100) / total_units)
      : module_progress.length > 0 ? Math.floor((completed_modules_count * 100) / module_progress.length) : 0;
    const course_progress_pct = Math.min(raw_progress_pct, 99);

    return {
      course_id: courseId,
      assignment_id: td.assignment_id,
      user_id: td.user_id,
      status: course_status,
      progress_percentage: course_status === 'COMPLETED' ? 100 : course_progress_pct,
      total_time_spent_seconds: total_time,
      // Which language version is being read.
      language_code: activeLanguageCode,
      is_mandatory: td.is_mandatory ?? false,
      certificate_issued: td.certificate_issued ?? false,
      attempts: td.attempts ?? 0,
      started_at: td.started_at || now,
      last_accessed_at: now,
      completed_at: course_status === 'COMPLETED' ? (td.completed_at || now) : undefined,
      total_modules: module_progress.length,
      completed_modules: completed_modules_count,
      current_module_id: selectedModule?._id,
      module_progress,
    };
    // activeLanguageCode is a dependency: without it this callback keeps the
    // language from the render it was created in, and a learner who switched
    // would keep reporting the previous one.
  }, [courseId, moduleContentCache, selectedModule, completionThreshold, activeLanguageCode]);

  // === TRACKING: revoked course — stop flushing and send the learner back ===
  // The tracking record is deleted when an assignment is revoked, so the server rejects
  // every flush with course_revoked. Retrying it forever would leave the learner in a
  // player they no longer have access to.
  const handleCourseRevoked = useCallback(() => {
    if (isRevokedRef.current) return;
    isRevokedRef.current = true;

    if (tickIntervalRef.current) {
      clearInterval(tickIntervalRef.current);
      tickIntervalRef.current = null;
    }

    toast.error('This course has been revoked');
    navigate('/learn/courses', { replace: true });
  }, [navigate]);

  // === TRACKING: tick() — send bulk update + fetch metrics ===
  const tick = useCallback(async () => {
    if (isCourseCompletedRef.current || isRevokedRef.current || !trackingDataRef.current || !isLearner || !courseId) return;

    const payload = buildBulkPayload();
    if (!payload) return;

    try {
      const response = await trackingService.bulkUpdateTracking(payload);
      // Reset accumulator on success
      setLocalTimeAccumulator(0);
      localTimeAccumulatorRef.current = 0;
      // API response is single source of truth
      setTrackingData(response);

      // Fetch updated metrics
      try {
        const metrics = await Leaderboardservice.getMyCourseMetrics(courseId);
        setMetricsData(metrics);
      } catch (metricsErr) {
        console.warn('[Tracking] Metrics fetch failed, continuing:', metricsErr);
      }

      // Re-check the completion requirements. The assessment player is nested too
      // deep to call back into this page, so the periodic tick is what notices a
      // newly passed assessment and clears the notice.
      try {
        await refetchCompletionRequirements();
      } catch {
        /* non-fatal — the next tick retries */
      }

      // Check if course completed
      if (response.status === 'COMPLETED') {
        setIsCourseCompleted(true);
        if (tickIntervalRef.current) {
          clearInterval(tickIntervalRef.current);
          tickIntervalRef.current = null;
        }
      }
    } catch (err) {
      if (isRevokedError(err)) {
        handleCourseRevoked();
        return;
      }
      // Don't reset accumulator on failure — retry on next tick
      console.warn('[Tracking] Bulk update failed, will retry:', err);
    }
  }, [buildBulkPayload, courseId, isLearner, refetchCompletionRequirements, handleCourseRevoked]);

  // === TRACKING: Initial fetch on mount ===
  useEffect(() => {
    if (!courseId || !isLearner) return;

    let cancelled = false;

    const fetchInitial = async () => {
      try {
        const [tracking, metrics] = await Promise.allSettled([
          trackingService.getMyCourseDetails(courseId),
          Leaderboardservice.getMyCourseMetrics(courseId),
        ]);

        if (cancelled) return;

        if (tracking.status === 'fulfilled') {
          setTrackingData(tracking.value);
          if (tracking.value.status === 'COMPLETED') {
            setIsCourseCompleted(true);
            // Already completed on load — mark ref so popup doesn't show
            prevCourseCompletedRef.current = true;
          }
        } else if (tracking.status === 'rejected') {
          const err = tracking.reason;
          const error_code = err?.response?.data?.error?.error_code;
          if (error_code === 'tracking_not_found') {
            if (!cancelled) {
              toast.error("You don't have access to this course");
              navigate('/learn/courses', { replace: true });
            }
            return;
          }
        }

        if (metrics.status === 'fulfilled') {
          setMetricsData(metrics.value);
        }
      } catch (err) {
        console.warn('[Tracking] Initial fetch failed:', err);
      } finally {
        if (!cancelled) setTrackingLoaded(true);
      }
    };

    fetchInitial();
    return () => { cancelled = true; };
  }, [courseId, isLearner]);

  // === TRACKING: 1-second local time accumulator ===
  // For video content: only accumulate when video is actually playing
  // For non-video content: accumulate whenever content item is selected
  useEffect(() => {
    if (!isLearner || isCourseCompleted || !trackingLoaded) return;

    const timer = setInterval(() => {
      const content = selectedContentItemRef.current;
      if (content && content.type !== 'assessment') {
        // Check if current content is video — only tick when video is playing
        const isVideoContent = content.type === 'video_presentation' || content.type === 'avatar_video' || !!content.media_assets?.s3_video_url;
        if (isVideoContent && !isVideoPlayingRef.current) return;

        setLocalTimeAccumulator(prev => prev + 1);
        localTimeAccumulatorRef.current += 1;
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [isLearner, isCourseCompleted, trackingLoaded]);

  // === TRACKING: 5-second bulk update interval ===
  useEffect(() => {
    if (!isLearner || isCourseCompleted || !trackingLoaded) return;

    tickIntervalRef.current = setInterval(() => {
      tick();
    }, 5000);

    return () => {
      if (tickIntervalRef.current) {
        clearInterval(tickIntervalRef.current);
        tickIntervalRef.current = null;
      }
    };
  }, [isLearner, isCourseCompleted, trackingLoaded, tick]);

  // === TRACKING: Poll for the certificate after completion ===
  // The certificate is issued asynchronously on the server, so whichever response flips the
  // course to COMPLETED still carries certificate_issued: false. Completion also shuts down
  // every refresh path — tick() early-returns on isCourseCompletedRef and its interval is
  // cleared — so nothing re-fetched and the "Course Completed!" card in the sidebar only
  // appeared after a manual reload, long after the celebration popup had come and gone.
  // Poll the read-only tracking endpoint until the certificate lands, then stop.
  useEffect(() => {
    if (!isLearner || !courseId || !isCourseCompleted) return;
    if (trackingData?.certificate_issued) return;

    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 15; // ~45s at 3s spacing, then give up quietly

    const pollInterval = setInterval(async () => {
      attempts += 1;
      if (attempts > MAX_ATTEMPTS) {
        clearInterval(pollInterval);
        return;
      }

      try {
        const fresh = await trackingService.getMyCourseDetails(courseId);
        if (cancelled) return;

        setTrackingData(fresh);
        if (fresh.certificate_issued) {
          clearInterval(pollInterval);
        }
      } catch {
        /* non-fatal — retry until MAX_ATTEMPTS */
      }
    }, 3000);

    return () => {
      cancelled = true;
      clearInterval(pollInterval);
    };
  }, [isLearner, courseId, isCourseCompleted, trackingData?.certificate_issued]);

  // === TRACKING: Unmount — fire-and-forget final flush ===
  useEffect(() => {
    return () => {
      if (localTimeAccumulatorRef.current > 0 && !isCourseCompletedRef.current && !isRevokedRef.current && trackingDataRef.current) {
        // Fire-and-forget: we can't await in cleanup
        const payload = buildBulkPayload();
        if (payload) {
          trackingService.bulkUpdateTracking(payload).catch(() => {});
        }
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // === POPUPS: Show course completion celebration when isCourseCompleted becomes true ===
  useEffect(() => {
    if (isCourseCompleted && !prevCourseCompletedRef.current && isLearner) {
      setShowCourseCompletion(true);
    }
    prevCourseCompletedRef.current = isCourseCompleted;
  }, [isCourseCompleted, isLearner]);

  // === POPUPS: Terms & Conditions gate ===
  // Once the learner has finished all content (modules 100% and no assessment left to
  // pass) but a T&C agreement is still pending, the course is held IN_PROGRESS on the
  // server. Present the agreement so they can accept and complete.
  useEffect(() => {
    if (!isLearner || isCourseCompleted) return;
    if (showTermsModal || acceptingTerms || suppressTermsModalRef.current) return;
    // Progress stays below 100 while T&C is pending, so check the modules directly.
    const modulesDone = !!trackingData && trackingData.total_modules > 0
      && trackingData.completed_modules >= trackingData.total_modules;
    if (modulesDone && !hasPendingRequirements && pendingTerms) {
      setPendingTermsCondition(pendingTerms);
      setShowTermsModal(true);
    }
  }, [trackingData, hasPendingRequirements, pendingTerms, isLearner, isCourseCompleted, showTermsModal, acceptingTerms]);

  // === POPUPS: Detect new badges from metricsData changes ===
  useEffect(() => {
    if (!metricsData?.new_badges || metricsData.new_badges.length === 0 || !isLearner) return;

    const unacknowledged = metricsData.new_badges.filter(b => !acknowledgedBadgeIds.has(b.badge_id));
    if (unacknowledged.length > 0) {
      setPendingBadges(unacknowledged);
      setShowBadgePopup(true);
    }
  }, [metricsData, isLearner, acknowledgedBadgeIds]);

  // === TRACKING: Manual content completion handler ===
  const handleContentComplete = useCallback((contentId: string) => {
    if (!trackingData) return;

    // Locally mark the lesson as COMPLETED — keep actual time_spent as-is
    setTrackingData(prev => {
      if (!prev) return prev;
      const updated = {
        ...prev,
        module_progress: prev.module_progress.map(mod => ({
          ...mod,
          lesson_progress: (mod.lesson_progress || []).map(lesson => {
            if (lesson.lesson_id !== contentId) return lesson;
            return {
              ...lesson,
              status: 'COMPLETED' as const,
              progress_percentage: 100,
              completed_at: lesson.completed_at || new Date().toISOString(),
            };
          }),
        })),
      };
      return updated;
    });

    // Trigger immediate flush
    setTimeout(() => tick(), 0);
  }, [trackingData, tick]);

  // === TRACKING: Manual course completion handler ===
  const handleCompleteCourse = useCallback(async () => {
    if (!courseId || !trackingData || completingCourse) return;

    // Custom completion gate: if a Terms & Conditions agreement is still pending,
    // present it instead of completing — the server would withhold completion
    // (and the certificate) until the learner agrees anyway.
    try {
      const gate = await courseService.getCompletionGate(courseId);
      const pendingTerms = gate.conditions?.find(
        (c) => c.type === 'terms_and_conditions' && !c.is_satisfied
      );
      if (pendingTerms) {
        setPendingTermsCondition(pendingTerms);
        setShowTermsModal(true);
        return;
      }
    } catch {
      /* gate check failed — fall through to the normal completion path */
    }

    setCompletingCourse(true);

    try {
      // Build payload with course status = COMPLETED
      const payload = buildBulkPayload();
      if (payload) {
        payload.status = 'COMPLETED';
        payload.progress_percentage = 100;
        payload.completed_at = new Date().toISOString();
        const response = await trackingService.bulkUpdateTracking(payload);
        setTrackingData(response);
        setLocalTimeAccumulator(0);
        localTimeAccumulatorRef.current = 0;
      }

      // Fetch final metrics
      try {
        const metrics = await Leaderboardservice.getMyCourseMetrics(courseId);
        setMetricsData(metrics);
      } catch { /* ignore */ }

      setIsCourseCompleted(true);
      if (tickIntervalRef.current) {
        clearInterval(tickIntervalRef.current);
        tickIntervalRef.current = null;
      }
    } catch (err) {
      if (isRevokedError(err)) {
        handleCourseRevoked();
        return;
      }
      console.error('[Tracking] Course completion failed:', err);
    } finally {
      setCompletingCourse(false);
    }
  }, [courseId, trackingData, completingCourse, buildBulkPayload, handleCourseRevoked]);

  // Learner agreed to the Terms & Conditions — record it, then finalize completion.
  const handleAcceptTerms = useCallback(async () => {
    if (!courseId || !pendingTermsCondition || acceptingTerms) return;
    setAcceptingTerms(true);
    try {
      await courseService.acceptTerms(courseId, pendingTermsCondition.id, pendingTermsCondition.version);
      // The T&C gate is now satisfied. Clear the hold locally (the query refetch is
      // async and the completion tick reads the ref) and stop the auto-open effect
      // from re-showing the modal in the gap before the gate query refreshes.
      hasPendingTermsRef.current = false;
      suppressTermsModalRef.current = true;
      setShowTermsModal(false);
      setPendingTermsCondition(null);
      refetchCompletionGate();
      // Re-run completion: it now passes the gate and completes the course on the
      // server (issuing the certificate).
      await handleCompleteCourse();
    } catch (err) {
      console.error('[Tracking] Failed to accept terms:', err);
      toast.error('Could not save your agreement. Please try again.');
    } finally {
      setAcceptingTerms(false);
    }
  }, [courseId, pendingTermsCondition, acceptingTerms, handleCompleteCourse, refetchCompletionGate]);

  // Handle module selection
  const handleModuleSelect = async (module: any) => {
    setSelectedModule(module);
    setShowingCourseOverview(false);

    // Use pre-loaded content from cache
    const cachedContent = moduleContentCache[module._id];
    if (cachedContent) {
      setSelectedModuleContent(cachedContent);
    } else {
      console.warn(`Module content not in cache: ${module._id}`);
      setSelectedModuleContent(null);
    }

    setSelectedContentItem(null);

    setExpandedModules(prev => ({
      ...prev,
      [module._id]: true
    }));
  };

  // Reset media player state when switching content
  const resetMediaPlayerState = () => {
    setCurrentAudioScene(0);
    setCurrentVideoScene(0);
    setIsAudioPlaying(false);
    setIsVideoPlaying(false);
  };

  // Handle content item selection — flush time on switch
  const handleContentItemSelect = async (contentItem: ContentItem) => {
    // A resume point belongs to ONE lesson. Opening a different lesson must
    // start it at the beginning, not part-way through because of an earlier
    // language switch.
    if (selectedContentItem?._id !== contentItem._id) {
      setVideoResume(undefined);
      videoProgressRef.current = { ratio: 0, playing: false };
    }

    // Flush accumulated time for the previous content item
    if (localTimeAccumulatorRef.current > 0 && selectedContentItem && selectedContentItem._id !== contentItem._id) {
      tick();
      setLocalTimeAccumulator(0);
      localTimeAccumulatorRef.current = 0;
    }

    // An item picked from another module (e.g. one only expanded in the sidebar)
    // must make that module current, or the breadcrumb and module card keep
    // showing the old one. Course pre/post assessments belong to no module.
    const ownerModule = (course?.modules || []).find((m: any) =>
      (m.content_items || moduleContentCache[m._id]?.module?.content_items || [])
        .some((ci: ContentItem) => ci._id === contentItem._id)
    );
    if (ownerModule && (selectedModule?._id || selectedModule?.id) !== ownerModule._id) {
      setSelectedModule(ownerModule);
      setSelectedModuleContent(moduleContentCache[ownerModule._id] || null);
      setExpandedModules(prev => ({ ...prev, [ownerModule._id]: true }));
    }

    setSelectedContentItem(contentItem);
    setShowingCourseOverview(false);
    resetMediaPlayerState();
  };

  // Toggle module expansion
  const toggleModuleExpansion = (moduleId: string) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleId]: !prev[moduleId]
    }));
  };

  // Set all expanded modules at once (for expand/collapse all)
  const setAllExpandedModules = (expanded: { [key: string]: boolean }) => {
    setExpandedModules(expanded);
  };

  // Show course overview
  /**
   * An assessment was just submitted. If it was the last required one, the server
   * has already completed the course and issued the certificate, so pull the fresh
   * tracking straight away rather than waiting for the next periodic tick — that is
   * what makes the completion popup appear immediately. Purely additive: the tick
   * still does its own refresh, so nothing in the existing flow changes.
   */
  const handleAssessmentSubmitted = useCallback(async () => {
    if (!courseId || !isLearner) return;
    try {
      const [requirements, tracking] = await Promise.allSettled([
        refetchCompletionRequirements(),
        trackingService.getMyCourseDetails(courseId),
      ]);
      void requirements;
      if (tracking.status === 'fulfilled') {
        setTrackingData(tracking.value);
        if (tracking.value.status === 'COMPLETED') setIsCourseCompleted(true);
      }
    } catch {
      /* non-fatal — the periodic tick picks it up */
    }
  }, [courseId, isLearner, refetchCompletionRequirements]);

  const handleShowCourseOverview = () => {
    setShowingCourseOverview(true);
    setSelectedModule(null);
    setSelectedModuleContent(null);
    setSelectedContentItem(null);
  };

  // Playback rate handlers
  const handleVideoPlaybackRateChange = () => {
    const speeds = [0.5, 0.75, 1, 1.25, 1.5, 2];
    const currentIndex = speeds.indexOf(videoPlaybackRate);
    const nextIndex = (currentIndex + 1) % speeds.length;
    setVideoPlaybackRate(speeds[nextIndex]);
  };

  const handleAudioPlaybackRateChange = () => {
    const speeds = [0.5, 0.75, 1, 1.25, 1.5, 2];
    const currentIndex = speeds.indexOf(audioPlaybackRate);
    const nextIndex = (currentIndex + 1) % speeds.length;
    setAudioPlaybackRate(speeds[nextIndex]);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-brand-purple-600 to-brand-indigo-700">
        <div className="text-center text-white">
          <Loader2 className="w-16 h-16 animate-spin mx-auto mb-4" />
          <p>Loading AI Learning Experience...</p>
          <p className="text-sm text-brand-purple-200 mt-2">Course ID: {courseId}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Failed to Load Course</h2>
          <p className="text-gray-600 mb-4">Course ID: {courseId}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-brand-purple-600 text-white rounded-lg hover:bg-brand-purple-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // No course found
  if (!course || !course.modules || course.modules.length === 0) {
    return (
      <div className="h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <BookOpen className="w-8 h-8 text-gray-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No Course Content Available</h2>
          <p className="text-gray-600">This course doesn't have any modules yet.</p>
        </div>
      </div>
    );
  }

  // === TRACKING: Derived values for sidebar ===
  const completedModules = new Set(
    trackingData?.module_progress
      ?.filter(m => m.status === 'COMPLETED')
      .map(m => m.module_id) || []
  );
  const courseProgress = trackingData?.progress_percentage || 0;
  const requiredAssessmentIds = new Set(
    (completionRequirements?.requirements || []).map(r => r.assessment_id)
  );
  const userLevel = metricsData?.course_level || 1;
  const userXP = metricsData?.course_xp || 0;
  const learningStreak = metricsData?.course_streak || 0;

  // Sidebar module list: course pre/post assessments are injected as
  // pseudo-modules around the real ones.
  const sidebarModules = (() => {
    const modules = course.modules || [];
    let result = [...modules];

    // If course has a pre_assessment, inject it as a pseudo-module at the beginning
    if (course.pre_assessment && course.pre_assessment.id) {
      const preAssessmentModuleId = `course_pre_assessment_${course.pre_assessment.id}`;
      result.unshift({
        _id: preAssessmentModuleId,
        title: course.pre_assessment.title || 'Course Pre-Assessment',
        description: course.pre_assessment.description,
        order: 0,
        module_number: 0,
        is_course_pre_assessment: true,
        pre_assessment: course.pre_assessment,
        content_items: [{
          _id: `assessment_${course.pre_assessment.id}_PRE_COURSE`,
          title: course.pre_assessment.title || 'Course Pre-Assessment',
          type: 'assessment',
          description: course.pre_assessment.description,
          duration: course.pre_assessment.time_limit_minutes
            ? { minutes: course.pre_assessment.time_limit_minutes }
            : undefined,
          estimated_time: course.pre_assessment.time_limit_minutes
            ? { minutes: course.pre_assessment.time_limit_minutes }
            : undefined,
          assessment_id: course.pre_assessment.id,
          assessment_type: 'PRE',
          assessment_level: 'COURSE',
        }]
      });
    }

    // If course has a post_assessment, inject it as a pseudo-module after the last module
    if (course.post_assessment && course.post_assessment.id) {
      const postAssessmentModuleId = `course_assessment_${course.post_assessment.id}`;
      result.push({
        _id: postAssessmentModuleId,
        title: course.post_assessment.title || 'Final Course Assessment',
        description: course.post_assessment.description,
        order: modules.length + 1,
        module_number: modules.length + 1,
        is_course_assessment: true,
        post_assessment: course.post_assessment,
        content_items: [{
          _id: `assessment_${course.post_assessment.id}_POST_COURSE`,
          title: course.post_assessment.title || 'Final Course Assessment',
          type: 'assessment',
          description: course.post_assessment.description,
          duration: course.post_assessment.time_limit_minutes
            ? { minutes: course.post_assessment.time_limit_minutes }
            : undefined,
          estimated_time: course.post_assessment.time_limit_minutes
            ? { minutes: course.post_assessment.time_limit_minutes }
            : undefined,
          assessment_id: course.post_assessment.id,
          assessment_type: 'POST',
          assessment_level: 'COURSE',
        }]
      });
    }

    return result;
  })();

  // Previous / Next (phone & tablet bar pinned under the player): every item in
  // sidebar order. With no lesson open, Next starts the selected module, or the course.
  type LessonEntry = { module: any; item: ContentItem };
  const itemsOf = (module: any): ContentItem[] =>
    module.content_items || moduleContentCache[module._id]?.module?.content_items || [];
  const lessonSequence: LessonEntry[] = sidebarModules.flatMap((module) =>
    itemsOf(module).map((item) => ({ module, item }))
  );
  const currentIndex = showingCourseOverview || !selectedContentItem
    ? -1
    : lessonSequence.findIndex((e) => e.item._id === selectedContentItem._id);
  const previousLesson = currentIndex > 0 ? lessonSequence[currentIndex - 1] : null;
  const nextLesson = currentIndex >= 0
    ? lessonSequence[currentIndex + 1] ?? null
    : lessonSequence.find((e) => !showingCourseOverview && e.module._id === selectedModule?._id)
      ?? lessonSequence[0] ?? null;

  // Same lock rules as the sidebar (CourseNavigation isModuleLocked /
  // isContentItemLocked) and the same toasts — keep the two in step.
  const isLessonDone = (moduleId: string, lessonId: string) =>
    !!trackingData?.module_progress
      ?.find((mp) => mp.module_id === moduleId)
      ?.lesson_progress?.some((lp) => lp.lesson_id === lessonId && lp.status === 'COMPLETED');

  const getLockReason = ({ module, item }: LessonEntry): string | null => {
    if (!isLearner || module.is_course_pre_assessment) return null;
    const realModules = sidebarModules.filter((m) => !m.is_course_assessment && !m.is_course_pre_assessment);
    if (module.is_course_assessment) {
      return realModules.every((m) => completedModules.has(m._id))
        ? null
        : 'Complete all modules to unlock this assessment';
    }
    const moduleIndex = realModules.findIndex((m) => m._id === module._id);
    if (moduleIndex > 0 && !completedModules.has(realModules[moduleIndex - 1]._id)) {
      return 'Complete the previous module to unlock this one';
    }

    const items = itemsOf(module);
    const lessons = items.filter((ci) => ci.type !== 'assessment');
    // A lesson opens once the lesson before it is done; the first is always open.
    const isLessonLocked = (lesson?: ContentItem) => {
      const i = lesson ? lessons.indexOf(lesson) : -1;
      return i > 0 && !isLessonDone(module._id, lessons[i - 1]._id);
    };
    if (item.type !== 'assessment') {
      return isLessonLocked(item) ? 'Complete the previous lesson to unlock this one' : null;
    }

    const at = items.indexOf(item);
    const isLessonLevel = item.assessment_level === 'CONTENT_ITEM';
    const locked =
      // Lesson pre-test opens with the lesson it precedes.
      isLessonLevel && item.assessment_type === 'PRE' ? isLessonLocked(items.slice(at + 1).find((ci) => ci.type !== 'assessment'))
      // Lesson post-test waits for the lesson right before it.
      : isLessonLevel && item.assessment_type === 'POST' ? at <= 0 || !isLessonDone(module._id, items[at - 1]._id)
      // Module post-test waits for every lesson in the module.
      : item.assessment_level === 'MODULE' && item.assessment_type === 'POST' ? !lessons.every((l) => isLessonDone(module._id, l._id))
      : false;
    return locked ? 'Complete the required lessons to unlock this assessment' : null;
  };

  // handleContentItemSelect switches to the item's module itself.
  const openLesson = (entry: LessonEntry) => {
    const lockReason = getLockReason(entry);
    if (lockReason) {
      toast.error(lockReason, { icon: '🔒', duration: 3000 });
      return;
    }
    handleContentItemSelect(entry.item);
  };
  const nextLocked = !!nextLesson && !!getLockReason(nextLesson);

  // Determine view type for UnifiedPlayerView
  const getViewType = (): 'course' | 'module' | 'content' | 'none' => {
    if (showingCourseOverview) return 'course';
    if (selectedContentItem) return 'content';
    if (selectedModule && selectedModuleContent) return 'module';
    return 'none';
  };

  // Main layout
  return (
    /* Below lg: cancel LearnerLayout's main padding (p-3 / sm:p-4) and fill the
       visible height under its 57px header (dvh, so the phone's address bar
       doesn't push the player off-screen). lg and up: unchanged. The admin
       preview (DashboardLayout) pads its main with p-4 on mobile, not p-3.
       The dvh height is the fallback; below lg it is measured (playerRoot). */
    <div ref={setPlayerRoot} className={`bg-gray-50 flex ${isLearner ? '-m-3' : '-m-4'} sm:-m-4 lg:-m-5 xl:-m-6 h-[calc(100vh-57px)] supports-[height:100dvh]:h-[calc(100dvh-57px)] lg:h-[calc(100vh-4.75rem)] lg:supports-[height:100dvh]:h-[calc(100vh-4.75rem)]`}>
      {/* Main Player Area */}
      <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
        {/* Language switcher — a single compact control, the way a streaming
            player does it: one button showing what you are watching, a menu of
            what else is available. The earlier row of pills grew with every
            language and pushed the player down.

            Rendered only when this course HAS finished translations, so a
            single-language course looks exactly as it did before: no bar, no
            layout shift, nothing for existing learners to notice.

            Lives here rather than inside UnifiedPlayerView because that
            component is shared across several views, and threading a prop
            through it would touch code this feature has no business changing. */}
        {availableLanguages.length > 0 && (
          <div className="flex items-center justify-end px-4 py-2 bg-white border-b border-gray-200 flex-shrink-0">
            <div className="relative" ref={languageMenuRef}>
              <button
                onClick={() => setIsLanguageMenuOpen((open) => !open)}
                disabled={isAttemptInProgress}
                title={
                  isAttemptInProgress
                    ? 'Language stays the same until you submit this assessment'
                    : undefined
                }
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  isAttemptInProgress
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
                aria-haspopup="listbox"
                aria-expanded={isLanguageMenuOpen}
              >
                <Globe className="w-4 h-4 text-gray-400" />
                <span className="font-medium">{activeLanguageLabel}</span>
                <ChevronDown
                  className={`w-4 h-4 text-gray-400 transition-transform ${
                    isLanguageMenuOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {isLanguageMenuOpen && !isAttemptInProgress && (
                <div
                  role="listbox"
                  className="absolute right-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50 max-h-72 overflow-y-auto"
                >
                  <p className="px-3 py-1.5 text-[11px] font-semibold text-gray-400 uppercase tracking-wide">
                    Language
                  </p>

                  {/* The original is always first and always available. */}
                  <button
                    role="option"
                    aria-selected={!activeLanguageCode}
                    onClick={() => {
                      setIsLanguageMenuOpen(false);
                      handleSwitchLanguage(undefined);
                    }}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <span className="truncate">{originalLanguageLabel}</span>
                    {!activeLanguageCode && (
                      <Check className="w-4 h-4 text-brand-blue-600 flex-shrink-0" />
                    )}
                  </button>

                  {availableLanguages.map((lang) => {
                    const isActive =
                      activeLanguageCode?.toLowerCase() === lang.language_code?.toLowerCase();
                    return (
                      <button
                        key={lang.language_code}
                        role="option"
                        aria-selected={isActive}
                        onClick={() => {
                          setIsLanguageMenuOpen(false);
                          handleSwitchLanguage(lang.language_code);
                        }}
                        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <span className="truncate">
                          {languageLabelFor(lang)}
                        </span>
                        {isActive && <Check className="w-4 h-4 text-brand-blue-600 flex-shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* flex-1 min-h-0, and it matters.

            UnifiedPlayerView's own root is h-full — 100% of its PARENT, not of
            the space left over. While it was the only child of this column that
            was the same thing, so it worked. Adding the language switcher above
            it broke the assumption: the player kept asking for the full height,
            the column is overflow-hidden, and the bottom of the page (the
            “Back to Assessment Info” button) was clipped with no scrollbar.

            The wrapper gives it the REMAINING height instead. min-h-0 is not
            optional — a flex item defaults to min-height:auto and refuses to
            shrink below its content, which is what re-creates the clipping.

            Done here rather than in UnifiedPlayerView: that component is shared
            by other views that rely on h-full, and this feature has no business
            changing them. */}
        <div className="flex-1 min-h-0">
        <UnifiedPlayerView
          /* Keyed on the language THE LOADED DATA IS IN, so the player remounts
             when the translation arrives rather than when the language is
             picked. Keying on the URL remounted it against the still-English
             course, and the Arabic then landed as a prop change it had already
             read past — which is why the body stayed English until a reload. */
          key={courseData?.loadedLanguageCode || 'master'}
          viewType={getViewType()}
          course={course}
          courseId={courseId!}
          selectedModule={displayedModule}
          moduleContent={displayedModuleContent}
          contentItem={displayedContentItem}
          loadingModuleContent={loadingModuleContent}
          isLearner={isLearner}
          isScormCourse={isScormCourse}
          currentVideoScene={currentVideoScene}
          currentAudioScene={currentAudioScene}
          isVideoPlaying={isVideoPlaying}
          isAudioPlaying={isAudioPlaying}
          videoPlaybackRate={videoPlaybackRate}
          audioPlaybackRate={audioPlaybackRate}
          videoVolume={videoVolume}
          audioVolume={audioVolume}
          autoPlayNext={autoPlayNextScene}
          onVideoSceneChange={setCurrentVideoScene}
          onAudioSceneChange={setCurrentAudioScene}
          onVideoPlayPause={() => setIsVideoPlaying(prev => !prev)}
          onVideoPlayStateChange={(playing) => setIsVideoPlaying(playing)}
          onVideoProgressRatio={(ratio, playing) => {
            videoProgressRef.current = { ratio, playing };
          }}
          videoResumeRatio={videoResume?.ratio}
          videoResumeAutoPlay={videoResume?.playing}
          onAudioPlayPause={() => setIsAudioPlaying(prev => !prev)}
          onVideoPlaybackRateChange={handleVideoPlaybackRateChange}
          onAudioPlaybackRateChange={handleAudioPlaybackRateChange}
          onVideoVolumeChange={setVideoVolume}
          onAudioVolumeChange={setAudioVolume}
          onAutoPlayToggle={() => setAutoPlayNextScene(!autoPlayNextScene)}
          onSelectModule={handleModuleSelect}
          onSelectContentItem={handleContentItemSelect}
          progressData={trackingData}
          completionRequirements={completionRequirements}
          onAssessmentSubmitted={handleAssessmentSubmitted}
          onOpenCourseContent={() => setMobilePanel('content')}
          onOpenAIAssistant={() => setMobilePanel('ai')}
          onPrevious={previousLesson ? () => openLesson(previousLesson) : undefined}
          onNext={nextLesson ? () => openLesson(nextLesson) : undefined}
          nextLocked={nextLocked}
        />
        </div>
      </div>

      {/* Tabbed Sidebar */}
      <TabbedSidebar
        course={course}
        courseId={courseId!}
        modules={sidebarModules}
        selectedModule={selectedModule}
        selectedModuleContent={selectedModuleContent}
        selectedContent={selectedContentItem}
        expandedModules={expandedModules}
        completedModules={completedModules}
        loadingModuleContent={loadingModuleContent}
        userLevel={userLevel}
        userXP={userXP}
        learningStreak={learningStreak}
        courseProgress={courseProgress}
        userEmail={user?.email}
        userName={user?.full_name || user?.given_name || user?.email || 'Student'}
        showingCourseOverview={showingCourseOverview}
        moduleContentCache={moduleContentCache}
        progressData={trackingData}
        isCourseCompleted={isCourseCompleted}
        certificateIssued={!!trackingData?.certificate_issued}
        completingCourse={completingCourse}
        isLearner={isLearner}
        isScormCourse={isScormCourse}
        manualCompletionEnabled={completionGate?.manual_completion_enabled !== false}
        requiredAssessmentIds={requiredAssessmentIds}
        onModuleSelect={handleModuleSelect}
        onContentItemSelect={handleContentItemSelect}
        onToggleModule={toggleModuleExpansion}
        onSetAllExpandedModules={setAllExpandedModules}
        onShowCourseOverview={handleShowCourseOverview}
        onCompleteCourse={handleCompleteCourse}
        onContentComplete={handleContentComplete}
        mobilePanel={mobilePanel}
        onMobilePanelChange={setMobilePanel}
      />

      {/* Terms & Conditions agreement gate */}
      <TermsAgreementModal
        isOpen={showTermsModal}
        condition={pendingTermsCondition}
        accepting={acceptingTerms || completingCourse}
        onClose={() => {
          // Learner dismissed it — don't auto-reopen. They can bring it back via
          // the "Complete Course" button (which opens it directly).
          suppressTermsModalRef.current = true;
          setShowTermsModal(false);
        }}
        onAccept={handleAcceptTerms}
      />

      {/* Course Completion Celebration */}
      {showCourseCompletion && (
        <CourseCompletionCelebration
          courseId={courseId!}
          courseTitle={course?.title || 'Course'}
          courseDuration={`${course?.estimated_duration?.hours || 0}h`}
          moduleCount={course.modules?.length || 0}
          completionDate={new Date()}
          totalXP={metricsData?.course_xp || 0}
          accuracy={100}
          isLearner={isLearner}
          onClose={() => setShowCourseCompletion(false)}
          onDownloadCertificate={() => {
            console.log('Downloading certificate for course:', course?.title);
          }}
        />
      )}

      {/* Badge Earned Popup */}
      {showBadgePopup && pendingBadges.length > 0 && (
        <BadgePopup
          badges={pendingBadges}
          onClose={() => {
            // Mark these badges as acknowledged so they don't show again
            const badgeIds = pendingBadges.map(b => b.badge_id);
            setAcknowledgedBadgeIds(prev => {
              const next = new Set(prev);
              badgeIds.forEach(id => next.add(id));
              return next;
            });
            setShowBadgePopup(false);
            setPendingBadges([]);

            // Fire-and-forget acknowledge API call
            Leaderboardservice.acknowledgeBadges({
              badge_ids: badgeIds,
              source_type: 'COURSE',
              source_id: courseId,
            }).catch(() => {});
          }}
        />
      )}
    </div>
  );
}
